import { DEFAULT_CURRENCY, moneyFormat } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import DotsVertical from "@redotech/redo-icons/dots-vertical.svg";
import FileSearch from "@redotech/redo-icons/file-search-02.svg";
import {
  ServiceInvoiceType,
  SupportAiSubscriptionInvoice,
  SupportAiUsageInvoice,
  SupportBaseInvoice,
  SupportVoiceSubscriptionInvoice,
  SupportVoiceUsageInvoice,
} from "@redotech/redo-model/billing/service-invoice";
import { SortDirection, TableSort } from "@redotech/redo-model/tables/table";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import {
  RedoTableBadgeCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { PageMainContentRefContext } from "@redotech/redo-web/page";
import { ServiceInvoiceModal } from "@redotech/redo-web/service-invoice/service-invoice-modal";
import { ColumnAlignment, TableFetcher } from "@redotech/redo-web/table";
import { Column2, Table2 } from "@redotech/redo-web/table2";
import { getDateString } from "@redotech/util/date-format";
import { memo, useMemo, useRef, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

type SupportInvoice =
  | SupportBaseInvoice
  | SupportAiSubscriptionInvoice
  | SupportAiUsageInvoice
  | SupportVoiceSubscriptionInvoice
  | SupportVoiceUsageInvoice;

interface SupportInvoiceRow {
  _id: string;
  lineItems: SupportInvoice["lineItems"];
  paid: boolean;
  dateOfLastBillingAttempt: Date | null | undefined;
  billingDueDate: Date;
  type: ServiceInvoiceType;
  refunded?: boolean;
}

class SupportInvoiceFetcher implements TableFetcher<SupportInvoiceRow> {
  public rows: SupportInvoiceRow[] = [];

  constructor(
    private readonly client: any,
    private readonly teamId: string,
    private updateItems: (items: SupportInvoiceRow[]) => void,
  ) {}

  async counts(): Promise<{ [key: string]: number }> {
    return { total: this.rows.length };
  }

  async *data(
    primaryFilter: string,
    filters: Record<string, string>,
    search: string | undefined,
    sort: any,
    pageSize: number,
    pageNumber: number,
    signal: AbortSignal,
  ): AsyncIterator<{
    items: SupportInvoiceRow[];
    refresh: () => Promise<SupportInvoiceRow[]>;
  }> {
    try {
      const result = await this.client.getSupportInvoices(
        { teamId: this.teamId },
        { signal },
      );

      const allInvoices: SupportInvoiceRow[] = [
        ...result.supportBaseInvoices.map(
          (inv: SupportBaseInvoice): SupportInvoiceRow => ({
            _id: inv._id,
            lineItems: inv.lineItems,
            paid: inv.billedSuccessfully,
            dateOfLastBillingAttempt: inv.dateOfLastBillingAttempt,
            billingDueDate: inv.billingDueDate,
            type: inv.kind,
            refunded: inv.refunded,
          }),
        ),
        ...result.supportAiInvoices.map(
          (
            inv: SupportAiSubscriptionInvoice | SupportAiUsageInvoice,
          ): SupportInvoiceRow => ({
            _id: inv._id,
            lineItems: inv.lineItems,
            paid: inv.billedSuccessfully,
            dateOfLastBillingAttempt: inv.dateOfLastBillingAttempt,
            billingDueDate: inv.billingDueDate,
            type: inv.kind,
            refunded: inv.refunded,
          }),
        ),
        ...result.supportVoiceInvoices.map(
          (
            inv: SupportVoiceSubscriptionInvoice | SupportVoiceUsageInvoice,
          ): SupportInvoiceRow => ({
            _id: inv._id,
            lineItems: inv.lineItems,
            paid: inv.billedSuccessfully,
            dateOfLastBillingAttempt: inv.dateOfLastBillingAttempt,
            billingDueDate: inv.billingDueDate,
            type: inv.kind,
            refunded: inv.refunded,
          }),
        ),
      ].sort(
        (a, b) =>
          new Date(b.billingDueDate).getTime() -
          new Date(a.billingDueDate).getTime(),
      );

      this.rows = allInvoices;
      this.updateItems(allInvoices);

      yield {
        items: allInvoices,
        refresh: async () => {
          this.rows = [];
          this.updateItems([]);
          return this.rows;
        },
      };
    } catch (error: any) {
      if (error.code === "ERR_CANCELED") {
        error.message = "Another request is in flight";
      }
      throw error;
    }
  }
}

export const SupportInvoices = memo(function SupportInvoices({
  teamId,
}: {
  teamId: string;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const pageMainContentRef = useRequiredContext(PageMainContentRefContext);
  const [activeServiceInvoice, setActiveServiceInvoice] = useState<
    SupportInvoiceRow | undefined
  >();
  const [refreshSymbol, setRefreshSymbol] = useState<symbol>(Symbol());

  const fetcher = useRef(
    new SupportInvoiceFetcher(rpcClient, teamId, () => {}),
  ).current;

  const handleToggleRefund = useHandler(
    async (invoice: SupportInvoiceRow, refunded: boolean) => {
      try {
        await rpcClient.toggleInvoiceRefund({
          teamId,
          invoiceId: invoice._id,
          refunded,
        });
        setRefreshSymbol(Symbol());
      } catch (error) {
        console.error("Failed to toggle refund status:", error);
      }
    },
  );

  const columns: Column2<SupportInvoiceRow>[] = useMemo(
    () => [
      {
        key: "type",
        title: "Type",
        width: 150,
        alignment: ColumnAlignment.LEFT,
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell
            text={row.type
              .replace(/-/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}
          />
        ),
      },
      {
        key: "date",
        title: "Date",
        width: 150,
        alignment: ColumnAlignment.LEFT,
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell
            text={getDateString(
              row.dateOfLastBillingAttempt || row.billingDueDate,
            )}
          />
        ),
      },
      {
        key: "amount",
        title: "Amount",
        width: 150,
        alignment: ColumnAlignment.LEFT,
        renderNormalCell: ({ row }) => {
          const totalCost = row.lineItems.reduce(
            (acc, item) => acc + (item.price ?? 0),
            0,
          );
          const costRoundedToNearestCent = Math.floor(totalCost * 100) / 100;
          const displayAmount = row.refunded
            ? -costRoundedToNearestCent
            : costRoundedToNearestCent;

          return (
            <RedoTableTextCell
              text={moneyFormat({
                amount: displayAmount,
                currency: DEFAULT_CURRENCY,
              })}
            />
          );
        },
      },
      {
        key: "status",
        title: "Status",
        width: 120,
        alignment: ColumnAlignment.LEFT,
        renderNormalCell: ({ row }) => {
          const badges = [];

          if (row.refunded) {
            badges.push({ text: "Refunded", color: "gray" as const });
          } else if (row.paid) {
            badges.push({ text: "Paid", color: "success" as const });
          } else {
            badges.push({ text: "Unpaid", color: "warning" as const });
          }

          return <RedoTableBadgeCell badges={badges} />;
        },
      },
      {
        key: "view",
        title: "",
        width: 50,
        alignment: ColumnAlignment.CENTER,
        renderNormalCell: ({ row }) => (
          <RedoButton
            hierarchy="tertiary"
            IconLeading={FileSearch}
            onClick={() => setActiveServiceInvoice(row)}
            size="sm"
          />
        ),
      },
      {
        key: "actions",
        title: "",
        width: 50,
        alignment: ColumnAlignment.CENTER,
        renderNormalCell: ({ row }) => (
          <InvoiceActionsMenu
            invoice={row}
            onToggleRefund={handleToggleRefund}
          />
        ),
      },
    ],
    [handleToggleRefund],
  );

  const sortDefault: TableSort = { key: "date", direction: SortDirection.DESC };

  return (
    <>
      <Card p="xl" radius="sm">
        <Table2<SupportInvoiceRow>
          columns={columns}
          fetcher={fetcher}
          headerTitle="Support Invoices"
          refreshSymbol={refreshSymbol}
          scrollAreaRef={pageMainContentRef}
          size="sm"
          sortDefault={sortDefault}
        />
      </Card>
      <ServiceInvoiceModal
        activeItem={activeServiceInvoice}
        closeModal={() => setActiveServiceInvoice(undefined)}
      />
    </>
  );
});

const InvoiceActionsMenu = memo(function InvoiceActionsMenu({
  invoice,
  onToggleRefund,
}: {
  invoice: SupportInvoiceRow;
  onToggleRefund: (invoice: SupportInvoiceRow, refunded: boolean) => void;
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  return (
    <Flex align="center" justify="center">
      <RedoButton
        hierarchy="tertiary"
        IconLeading={DotsVertical}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          setDropdownOpen(!dropdownOpen);
        }}
        size="sm"
      />
      <RedoListDropdown
        dropdownAnchor={anchorEl}
        dropdownOpen={dropdownOpen}
        dropdownPlacement="bottom-end"
        items={[
          {
            id: "toggle-refund",
            value: invoice,
            type: "text",
            text: invoice.refunded
              ? "Mark as not refunded"
              : "Mark as refunded",
            supportingText: invoice.refunded
              ? "This will mark the invoice as NOT refunded in our system. This does not affect any actual payment transactions."
              : "This will mark the invoice as refunded in our system. This does NOT actually refund the charge - it only updates our records.",
          },
        ]}
        itemSelected={() => {
          onToggleRefund(invoice, !invoice.refunded);
          setDropdownOpen(false);
        }}
        setDropdownOpen={setDropdownOpen}
      />
    </Flex>
  );
});
